import {Subject}                                                   from 'rxjs';
import {HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse} from '@angular/common/http';
import {Injectable}                                                from '@angular/core';

/**
 * Service for simulating file upload operations using fake HTTP event streams.
 * Manages upload progress, completion, and error states for multiple file uploads.
 */
@Injectable()
export class ApUploadFakeStreamService {
  /** Map storing active upload streams indexed by file Id */
  private uploadStreams = new Map<string, Subject<HttpEvent<any>>>();

  /**
   * Creates a new upload stream for a specific file
   * @param fileId Unique identifier for the file
   */
  public createUploadStream(fileId: string): void {
    const subject = new Subject<HttpEvent<any>>();
    this.uploadStreams.set(fileId, subject);
  }

  /**
   * Removes an upload stream for a specific file
   * @param fileId Unique identifier for the file
   */
  public removeUploadStream(fileId: string): void {
    this.uploadStreams.delete(fileId);
  }

  /**
   * Retrieves the upload stream for a specific file
   * @param fileId Unique identifier for the file
   * @returns Subject representing the upload stream or undefined if not found
   */
  public getUploadStream(fileId: string): Subject<HttpEvent<any>> | undefined {
    return this.uploadStreams.get(fileId);
  }

  /**
   * Updates upload progress for a specific file
   * @param fileId Unique identifier for the file
   * @param progress Current progress value (0-100)
   */
  public updateProgress(fileId: string, progress: number): void {
    const stream = this.uploadStreams.get(fileId);
    if (stream) {
      stream.next({
        loaded: progress,
        total: 100,
        type: HttpEventType.UploadProgress
      } as HttpProgressEvent);
    }
  }

  /**
   * Marks an upload as complete and emits success response
   * @param fileId Unique identifier for the file
   * @param response Optional response data to include in success event
   */
  public completeUpload(fileId: string, response?: any): void {
    const stream = this.uploadStreams.get(fileId);
    if (stream) {
      stream.next(new HttpResponse({
        status: 200,
        body: response
      }));
      stream.complete();
      this.removeUploadStream(fileId);
    }
  }

  /**
   * Marks an upload as failed and emits error event
   * @param fileId Unique identifier for the file
   * @param error Optional error data to include in error event
   */
  public failUpload(fileId: string, error?: any): void {
    const stream = this.uploadStreams.get(fileId);
    if (stream) {
      stream.error(error);
      this.removeUploadStream(fileId);
    }
  }

  /**
   * Removes all active upload streams
   */
  public clearAllUploadStreams(): void {
    this.uploadStreams.clear();
  }
}
